/** Auto-run: advance the clock on a timer.
 *
 * Costs no network traffic. The MTM curve for the whole session is
 * already drawn from one request, so playing forward is purely local —
 * the chain refetches per minute exactly as a manual step does, and
 * nothing else moves.
 *
 * Stops itself at the close rather than hammering a clamped clock: the
 * step function pins at 15:29, so without this the timer would keep
 * firing forever against a value that no longer changes.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { SESSION_CLOSE, atLimit } from "@/lib/simulatorClock";

/** One minute of session per tick. Slow enough to read the chain as it
 * moves, fast enough to cross a session in about a minute. */
const TICK_MS = 150;

export interface AutoRun {
  running: boolean;
  toggle: () => void;
  stop: () => void;
}

export function useAutoRun(at: string, step: (minutes: number) => void): AutoRun {
  const [running, setRunning] = useState(false);
  // The stepper changes identity every render; holding it in a ref keeps
  // the interval from being torn down and rebuilt on each tick.
  const stepRef = useRef(step);
  stepRef.current = step;

  const stop = useCallback(() => {
    setRunning(false);
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      stepRef.current(1);
    }, TICK_MS);
    return () => {
      clearInterval(id);
    };
  }, [running]);

  // At the close there is nowhere left to go, and a timer firing into a
  // clamped clock looks like a hang rather than an ending.
  useEffect(() => {
    if (running && (at === SESSION_CLOSE || atLimit(at, 1))) setRunning(false);
  }, [running, at]);

  const toggle = useCallback(() => {
    setRunning((current) => !current);
  }, []);

  return { running, toggle, stop };
}
